import React from "react";
import { useQuery } from "@apollo/client";
import { useParams, Link } from "react-router-dom";

import qs from "query-string";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { QUERY_ERROR_LOG } from "../../../config/graphql/query";

import {
  formatJson,
  methodAndEndpoint,
  LogBlocks,
  LogField,
  LogFields,
} from "../../../components/LogDetail";

const ErrorLog = React.memo(() => {
  const { id, type } = useParams<{ id: string; type: LevelErrorType }>();

  const { t } = useTranslation(["errorLogs", "common"]);

  const { data } = useQuery<{ errorLog?: IErrorLog }>(QUERY_ERROR_LOG, {
    skip: !id,
    variables: { id, type },
  });

  const log = data?.errorLog;

  // The rows of one request are all written on the day it ran.
  const requestLink =
    log?.requestId &&
    `/errorLogs?${qs.stringify({
      logErrorType: type,
      requestId: log.requestId,
      startDate: dayjs(log.createdAt).startOf("day").format(),
      endDate: dayjs(log.createdAt).endOf("day").format(),
    })}`;

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item">
            <Link to="/errorLogs">{t("errorLogs:errorLog.nav.errorLogs")}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {t("errorLogs:errorLog.nav.errorLog")}
          </li>
        </ol>
      </nav>
      <LogFields>
        <LogField label={t("errorLogs:errorLog.card.created")}>
          {log &&
            dayjs(log.createdAt).local().format("DD-MM-YYYY HH:mm:ss.SSS")}
        </LogField>
        <LogField label={t("errorLogs:errorLog.card.errorMessage")}>
          {log?.errorMessage}
          {!!log?.fatal && (
            <span className="badge badge-danger ml-2">
              {t("errorLogs:errorLogs.fatal")}
            </span>
          )}
        </LogField>
        <LogField label={t("errorLogs:errorLog.card.error")}>
          {[log?.errorName, log?.errorCode].filter(Boolean).join(" · ")}
        </LogField>
        {!!log?.suppressed && (
          <LogField label={t("errorLogs:errorLog.card.repeated")}>
            {t("errorLogs:errorLogs.suppressed", { count: log.suppressed })}
          </LogField>
        )}
        <LogField label={t("errorLogs:errorLog.card.source")}>
          {log?.source && t(`errorLogs:errorLogs.source.${log.source}`)}
        </LogField>
        <LogField label={t("errorLogs:errorLog.card.endpoint")}>
          {methodAndEndpoint(log?.method, log?.endpoint)}
        </LogField>
        {!!log?.operation && (
          <LogField label={t("errorLogs:errorLog.card.operation")}>
            {log.operation}
          </LogField>
        )}
        {!!log?.graphqlPath && (
          <LogField label={t("errorLogs:errorLog.card.graphqlPath")}>
            {log.graphqlPath}
          </LogField>
        )}
        {!!log?.jobId && (
          <LogField label={t("errorLogs:errorLog.card.jobId")}>
            {log.jobId}
          </LogField>
        )}
        <LogField label={t("errorLogs:errorLog.card.client")}>
          {log?.client?.title ?? log?.clientId}
        </LogField>
        <LogField label={t("errorLogs:errorLog.card.user")}>
          {log?.userEmail}
        </LogField>
        <LogField label={t("errorLogs:errorLog.card.ip")}>{log?.ip}</LogField>
        <LogField label={t("errorLogs:errorLog.card.userAgent")}>
          {log?.userAgent}
        </LogField>
        <LogField label={t("errorLogs:errorLog.card.requestId")}>
          {requestLink && (
            <>
              {log?.requestId}
              <Link to={requestLink} className="ml-3">
                {t("errorLogs:errorLog.card.requestRows")}
              </Link>
            </>
          )}
        </LogField>
      </LogFields>
      <LogBlocks
        blocks={[
          {
            key: "stack",
            title: t("errorLogs:errorLog.card.errorStack"),
            text: log?.errorStack,
          },
          {
            key: "payload",
            title: t("errorLogs:errorLog.card.payload"),
            text: formatJson(log?.payload),
          },
          {
            key: "external",
            title: t("errorLogs:errorLog.card.external"),
            text: formatJson(log?.external),
          },
        ]}
      />
    </div>
  );
});

export default ErrorLog;
